"use client";

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { Subscription } from "@/types/user";
import { userService } from "@/lib/services/userService";
import { getStripe } from "@/lib/stripe";
import { useAuth } from "./AuthContext";

interface SubscriptionContextType {
  subscription: Subscription | null;
  isProMember: boolean;
  openUpgradeModal: () => void;
  closeUpgradeModal: () => void;
  isUpgradeModalOpen: boolean;
  upgradeSubscription: () => Promise<boolean>;
  cancelSubscription: () => Promise<boolean>;
  manageSubscription: () => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined
);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // Derived state
  const isProMember = subscription?.tier === "pro";

  // Fetch subscription data on mount and when auth state changes
  useEffect(() => {
    const fetchSubscription = async () => {
      // Don't attempt to fetch if still loading auth state
      if (authLoading) return;
      
      // Only fetch subscription if authenticated
      if (!isAuthenticated) {
        setSubscription(null);
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      try {
        const response = await userService.getUserSubscription();
        if (response.success) {
          setSubscription(response.data);
          console.log("SubscriptionContext - Setting subscription:", response.data);
          setError(null);
        } else {
          setError(response.error || "Failed to fetch subscription");
        }
      } catch (err) {
        setError("An unexpected error occurred");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscription();
  }, [isAuthenticated, authLoading]);

  // Check for success from Stripe redirect
  useEffect(() => {
    const checkSessionStatus = async () => {
      // Don't attempt to process if not authenticated or still loading auth
      if (!isAuthenticated || authLoading) return;
      
      // Check if redirected from Stripe with a session ID
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        
        if (sessionId) {
          setIsLoading(true);
          try {
            // Verify the session with your backend
            const response = await fetch('/api/subscriptions/verify-session', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ sessionId }),
            });
            
            const data = await response.json();
            
            if (data.success) {
              // Refresh subscription data
              const subResponse = await userService.getUserSubscription();
              if (subResponse.success) {
                setSubscription(subResponse.data);
              }
              
              // Clear the URL parameter
              window.history.replaceState({}, document.title, window.location.pathname);
            }
          } catch (err) {
            console.error('Error verifying session:', err);
          } finally {
            setIsLoading(false);
          }
        }
      }
    };
    
    checkSessionStatus();
  }, [isAuthenticated, authLoading]);

  const openUpgradeModal = () => {
    setIsUpgradeModalOpen(true);
  };

  const closeUpgradeModal = () => {
    setIsUpgradeModalOpen(false);
  };

  const upgradeSubscription = async (): Promise<boolean> => {
    // If not authenticated, return false immediately
    if (!isAuthenticated) {
      setError("You must be signed in to upgrade your subscription");
      return false;
    }
    
    setIsLoading(true);
    setError(null);
    console.log("SubscriptionContext: Starting upgradeSubscription");
    
    try {
      // Get current user info
      console.log("SubscriptionContext: Getting current user info");
      const userResponse = await userService.getCurrentUser();
      console.log("SubscriptionContext: User response", userResponse);
      
      if (!userResponse.success) {
        const errMsg = "Unable to retrieve user information";
        console.error(errMsg, userResponse.error);
        setError(errMsg);
        return false;
      }
      
      const user = userResponse.data;
      
      // Create Stripe checkout session
      console.log("SubscriptionContext: Creating checkout session");
      const response = await fetch('/api/subscriptions/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          email: user.email
        }),
      });
      
      const data = await response.json();
      console.log("SubscriptionContext: Checkout session response", data);
      
      if (!data.success) {
        const errMsg = data.error || "Failed to create checkout session";
        console.error(errMsg);
        setError(errMsg);
        return false;
      }
      
      // Redirect to Stripe checkout
      console.log("SubscriptionContext: Getting Stripe instance");
      const stripe = await getStripe();
      if (!stripe) {
        const errMsg = "Failed to initialize payment processor";
        console.error(errMsg);
        setError(errMsg);
        return false;
      }
      
      console.log("SubscriptionContext: Redirecting to checkout with session", data.data.sessionId);
      const { error } = await stripe.redirectToCheckout({
        sessionId: data.data.sessionId
      });
      
      if (error) {
        const errMsg = error.message || "Payment failed";
        console.error("Stripe redirect error:", errMsg);
        setError(errMsg);
        return false;
      }
      
      console.log("SubscriptionContext: Redirect initiated successfully");
      return true;
    } catch (err) {
      const errMsg = "An unexpected error occurred";
      console.error("SubscriptionContext: Unexpected error in upgradeSubscription", err);
      setError(errMsg);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const manageSubscription = async (): Promise<boolean> => {
    // If not authenticated, return false immediately
    if (!isAuthenticated) {
      setError("You must be signed in to manage your subscription");
      return false;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Make sure we have a subscription with customer ID
      if (!subscription?.customerId) {
        setError("No active subscription found");
        return false;
      }
      
      // Create Stripe customer portal session
      const response = await fetch('/api/subscriptions/create-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: subscription.customerId,
          returnUrl: `${window.location.origin}/profile?tab=subscription`
        }),
      });
      
      const data = await response.json();
      
      if (!data.success) {
        setError(data.error || "Failed to create customer portal");
        return false;
      }
      
      // Redirect to Stripe Customer Portal
      window.location.href = data.data.url;
      return true;
    } catch (err) {
      setError("An unexpected error occurred");
      console.error(err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const cancelSubscription = async (): Promise<boolean> => {
    // If not authenticated, return false immediately
    if (!isAuthenticated) {
      setError("You must be signed in to cancel your subscription");
      return false;
    }
    
    setIsLoading(true);
    try {
      const response = await userService.cancelSubscription();
      if (response.success) {
        // Update the local subscription state to reflect cancellation
        if (subscription) {
          setSubscription({
            ...subscription,
            status: "canceled",
          });
        }
        return true;
      } else {
        setError(response.error || "Failed to cancel subscription");
        return false;
      }
    } catch (err) {
      setError("An unexpected error occurred");
      console.error(err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const contextValue = {
    subscription,
    isProMember,
    openUpgradeModal,
    closeUpgradeModal,
    isUpgradeModalOpen,
    upgradeSubscription,
    cancelSubscription,
    manageSubscription,
    isLoading,
    error,
  };


  return (
    <SubscriptionContext.Provider
      value={contextValue}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider"
    );
  }
  return context;
};
